import { Box, Stack } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import TextEllipsis from 'core/elements/TextEllipsis';
import { Direction } from 'theme/enums';

interface IProps {
  subtitle: string;
  maxRows: number;
  isError?: boolean;
}

const SUBTITLE_LINE_HEIGHT = 20;

// An element, not a string: the core ellipsis parses strings as HTML, and subtitles carry
// user-entered and external text. A failure reason reads "Error <reason>": the label stays whole,
// the reason is cut on its own, in the danger colour (step 600 keeps its contrast on the danger
// tint in both modes).
export const NotificationSubtitle = ({ subtitle, maxRows, isError = false }: IProps) => {
  const { t } = useTranslation();

  if (!isError) {
    return (
      <TextEllipsis
        content={<span>{subtitle}</span>}
        maxRows={maxRows}
        lineHeight={SUBTITLE_LINE_HEIGHT}
        followCursor={false}
        sx={{ fontSize: 'sm', color: 'text.secondary', overflowWrap: 'anywhere' }}
      />
    );
  }

  return (
    <Stack
      direction={Direction.ROW}
      spacing={0.5}
      sx={{
        minWidth: 0,
        fontSize: 'sm',
        lineHeight: `${SUBTITLE_LINE_HEIGHT}px`,
        color: 'text.secondary',
      }}
    >
      <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
        {t('labelBellNotificationError')}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <TextEllipsis
          content={
            <Box
              component="span"
              data-testid="notificationErrorMessage"
              sx={{ color: 'danger.600' }}
            >
              {subtitle}
            </Box>
          }
          maxRows={maxRows}
          lineHeight={SUBTITLE_LINE_HEIGHT}
          followCursor={false}
          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
        />
      </Box>
    </Stack>
  );
};
